// Cuestionario por pasos ANTES de cada cotización (decisiones de Diego, PLAN-NOCHE §2). Se adapta a lo que contestas:
// negocio → giro; cliente nuevo → estimación del consumo; ampliación → el sistema que ya tiene; GDMTO/GDMTH → "sube los
// 12 recibos"; baterías o aislado → qué equipos se respaldan (contrato v2: cuestionario.respaldo). El borrador sin enviar se guarda en este navegador para no perderlo si se cierra la pestaña.
// También sirve para EDITAR el cuestionario de una cotización ya creada (una sola edición sobre /cuestionario).
import { useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { ArrowLeft, ArrowRight, BatteryCharging, BatteryPlus, Check, Factory, Gauge, House, Mountain, Plus, Store, Sun, SunMedium, Trash2 } from 'lucide-react';
import {
  DETALLE_ESTRUCTURA,
  ETIQUETA_ESTRUCTURA,
  ETIQUETA_FASES,
  ETIQUETA_REGIMEN_FISCAL,
  ETIQUETA_TENSION,
  FASES_SERVICIO,
  FASES_EN_PANTALLA,
  TENSIONES_EN_PANTALLA,
  REGIMENES_FISCALES,
  TARIFAS_CASA,
  TARIFAS_NEGOCIO,
  TENSIONES_POR_FASES,
  TIPOS_ESTRUCTURA,
  TENSIONES_TRANSFORMADOR,
  ETIQUETA_NO_SE_TRANSFORMADOR,
  estructuraSoloNegocio,
  etapaDe,
  tensionDeTransformador,
  formatoPara,
  mensajeFaltaWhatsapp,
  numero,
  pdfTodaviaNoSale,
  piezasAmpliacionVacia,
  MENSAJE_RECORTE_LE_CABEN,
  MENSAJE_RECORTE_PIDE_12,
  MENSAJE_RECORTE_SIN_NOCHE,
} from '@cotizador/compartido';
import type { Cotizacion, FasesServicio, Tarifa, TensionServicio, TipoEstructura, Usuario } from '@cotizador/compartido';
import { api } from '../../api/consultas.ts';
import { Enlace, navegar, usarRuta } from '../../ruteo.tsx';
import { usarUsuario } from '../../sesion.tsx';
import { Boton, BotonConfirmar, CampoArea, CampoSelect, CampoTexto, Cargando, ErrorEnLinea, Interruptor, Segmentado } from '../../componentes/ui.tsx';
import { SelectorVendedor, cotizaANombreDeOtros } from '../../componentes/SelectorVendedor.tsx';
import {
  PASOS,
  QUE_COTIZA_DISPONIBLE,
  queCotizaOpciones,
  cargaRespaldoVacia,
  cerrarInicio,
  conEnfoque,
  enfoqueConPaneles,
  enfoqueDe,
  formaDesde,
  migrarBorrador,
  panelesFueraDeEnfoque,
  pdfTodaviaNoSaleForma,
  queCotizaSinPdf,
  mediaTensionForma,
  formaVacia,
  aumentoEnForma,
  modoAumentoForma,
  horasEnGris,
  kwhMesDeEquiposAislado,
  llevaPaneles,
  llevaRespaldo,
  esAmpliacionForma,
  negocioDeForma,
  numeroDe,
  pasoDeCampo,
  recibosPorMedidor,
  validar,
} from '../../logica/cuestionario.ts';
import type { ClienteTipo, ErroresForma, FormaCargaRespaldo, FormaCuestionario, Paso, QueCotiza } from '../../logica/cuestionario.ts';
import { borrarBorradorLocal, guardarBorradorLocal, leerBorradorLocal } from '../../logica/borrador-local.ts';
import { NombresSitios, problemaSitios } from './PreguntaSitios.tsx';
import { cuestionariosDeSitios, diferenciaSitio, opcionesTarifa, sitioVacio, textoSitiosResumen } from '../../logica/sitios.ts';
import { EstimarConIA } from './EstimarConIA.tsx';
import { AumentoConIA } from './AumentoConIA.tsx';
import { firmaConsumos } from '../../logica/consumos-ia.ts';
import { ETIQUETA_FORMATO } from '../../logica/etiquetas.ts';
import { pedirVistaPrecios } from '../../logica/vista-precios.ts';
import { bateriaDeVista, claveBateria, kwhCorto, rangoAmpliacion, textoBateria } from '../../logica/ampliacion.ts';
import type { BateriaVista } from '../../logica/ampliacion.ts';
import type { SitiosForma } from './PreguntaSitios.tsx';

/** kW conectados de los equipos a respaldar (para enseñarlo mientras se llena; el motor hace los niveles). */
function kwDeCargas(cargas: readonly FormaCargaRespaldo[]): number | null {
  let w = 0;
  for (const c of cargas) {
    const n = numeroDe(c.cantidad);
    const p = numeroDe(c.w);
    if (n === null || p === null || !Number.isFinite(n) || !Number.isFinite(p)) return null;
    w += n * p;
  }
  return cargas.length ? w / 1000 : null;
}

const TITULO_PASO: Record<Paso, string> = { inicio: 'Qué cotizas', cliente: 'Cliente', situacion: 'Situación', notas: 'Notas' };
/** Ampliación de baterías: su tercer paso es la batería y cuántas tiene y suma (no hay recibo ni consumo que preguntar). */
const TITULO_PASO_AMPLIACION: Record<Paso, string> = { ...TITULO_PASO, situacion: 'Baterías' };

// TODO SE ELIGE DESDE EL INICIO (Diego, 13-oct, 22:45; ADENDA): tipo de cliente, qué se cotiza y los casos especiales van en el
// primer paso, con tarjetas grandes, para que el resto del cuestionario se enfoque ahí (lo que no aplica ni se pregunta).
export const ETIQUETA_CLIENTE: Record<ClienteTipo, string> = { casa: 'Casa', comercio: 'Comercio', industria: 'Industria' };
const DETALLE_CLIENTE: Record<ClienteTipo, string> = { casa: 'Tarifa doméstica', comercio: 'Negocio, baja tensión', industria: 'Planta o media tensión' };
const ICONO_CLIENTE: Record<ClienteTipo, ReactNode> = { casa: <House aria-hidden />, comercio: <Store aria-hidden />, industria: <Factory aria-hidden /> };
export const ETIQUETA_QUE: Record<QueCotiza, string> = {
  paneles: 'Solo paneles',
  paneles_baterias: 'Paneles + baterías',
  baterias: 'Solo baterías de respaldo',
  aislado: 'Aislado (sin CFE)',
  ampliacion_baterias: 'Ampliación de baterías',
  peak_shaving: 'Recorte de punta',
};
const DETALLE_QUE: Record<QueCotiza, string> = {
  paneles: 'Interconectado a CFE: baja el recibo',
  paneles_baterias: 'Híbrido: ahorra y respalda toda la casa o el negocio',
  baterias: 'Para los apagones, sin paneles (no se revisa techo)',
  aislado: 'Sin red de CFE: rancho, bomba, cabaña (sin recibo)',
  ampliacion_baterias: 'Sumar baterías a un sistema que ya tiene (sin recibo)',
  peak_shaving: 'Batería grande para la punta de la noche (GDMTH). Industria que trabaja de noche, sin techo o que ya tiene paneles.',
};
/** Verificación adversarial del 14-oct: híbrido y aislado se cotizan (opciones, CRM) pero el PDF todavía no sale; la tarjeta
 *  lo dice ANTES de elegir, como peak shaving dice «Próximamente» (logica/cuestionario.ts → queCotizaSinPdf). */
const SIN_PDF_TODAVIA = 'Sin PDF todavía: la propuesta la arma Diego';
const detalleQue = (q: QueCotiza): ReactNode =>
  queCotizaSinPdf(q) ? (
    <>
      {DETALLE_QUE[q]}
      <span className="segmentado__aviso">{SIN_PDF_TODAVIA}</span>
    </>
  ) : (
    DETALLE_QUE[q]
  );
const ICONO_QUE: Record<QueCotiza, ReactNode> = {
  paneles: <Sun aria-hidden />,
  paneles_baterias: <SunMedium aria-hidden />,
  baterias: <BatteryCharging aria-hidden />,
  aislado: <Mountain aria-hidden />,
  ampliacion_baterias: <BatteryPlus aria-hidden />,
  peak_shaving: <Gauge aria-hidden />,
};

/** Ampliación de baterías: lo que suele importar al sumar baterías (no hay recibo ni techo que revisar). */
const IDEAS_NOTAS_AMPLIACION = [
  'Se queda sin luz en la madrugada con los aires prendidos.',
  'Quiere que le alcance hasta que salga el sol.',
  'Tiene monitoreo: pedir acceso para la noche medida.',
  'El gabinete tiene espacio para las baterías nuevas.',
];

const IDEAS_NOTAS = [
  'Va a comprar un auto eléctrico.',
  'Techo de lámina; hay que revisar la estructura.',
  'Tiene minisplits en el techo que habría que mover.',
  'Quiere pagar a meses.',
  'Le preocupa el aumento de tarifa en verano.',
  'Va a construir otro cuarto el próximo año.',
];

/** Borrador sin enviar de ESTE usuario (la llave lleva su id; salir borra todos: logica/borrador-local.ts). */
function leerBorrador(usuarioId: string): { forma: FormaCuestionario; paso: Paso } | null {
  const j = leerBorradorLocal(usuarioId);
  // Un borrador de antes del paso «Qué cotizas» puede venir en el paso «tipo» (ya no existe): vuelve al primero.
  const paso = (j?.paso as string) === 'tipo' ? 'inicio' : j?.paso;
  if (!j || !j.forma || !paso || !PASOS.includes(paso)) return null;
  // De antes del paso «Qué cotizas»: se deduce el enfoque y se cierra el primer paso (logica/cuestionario.ts → migrarBorrador).
  return migrarBorrador({ ...formaVacia(), ...j.forma }, paso);
}

/** z58: cliente nuevo (sin recibos): lo que no cambia la cotización se contesta solo, con un valor claro y editable en
 *  «Situación»: precotización (la visita aún no se hace), el recibo a nombre del cliente y el consumo con la IA. */
function omisionesClienteNuevo(f: FormaCuestionario): FormaCuestionario {
  if (!f.esNuevo) return f;
  return {
    ...f,
    visita: f.visita ?? false,
    titularEsCliente: f.titularEsCliente ?? true,
    metodoEstimacion: f.kwhMes.trim() ? f.metodoEstimacion : 'cuadro_cargas',
  };
}

/** z58: aislado: la descripción para la IA sale de los equipos que ya capturó el vendedor. */
function descripcionDeEquipos(cargas: readonly FormaCargaRespaldo[]): string {
  const equipos = cargas
    .filter((c) => c.equipo.trim())
    .map((c) => {
      const dia = c.horas_de_dia?.trim();
      const noche = c.horas_noche?.trim();
      const horas = dia && noche ? `, ${dia} h de día y ${noche} de noche` : dia ? `, ${dia} h de día` : noche ? `, ${noche} h de noche` : '';
      return `${c.cantidad.trim() || '1'} ${c.equipo.trim()}${c.w.trim() ? ` de ${c.w.trim()} W` : ''}${horas}`;
    });
  return equipos.length ? `Casa o rancho sin CFE (aislado) con: ${equipos.join('; ')}` : '';
}

function SiNo({ etiqueta, valor, alCambiar, si, no, ayuda, error }: { etiqueta: ReactNode; valor: boolean | null; alCambiar: (v: boolean) => void; si: ReactNode; no: ReactNode; ayuda?: ReactNode; error?: string | null }) {
  return (
    <Segmentado<'si' | 'no'>
      etiqueta={etiqueta}
      valor={valor === null ? null : valor ? 'si' : 'no'}
      alCambiar={(v) => alCambiar(v === 'si')}
      opciones={[
        { valor: 'si', texto: si },
        { valor: 'no', texto: no },
      ]}
      ayuda={ayuda}
      error={error}
      columnas={2}
    />
  );
}

export function NuevaCotizacion({ editarId }: { editarId?: string }) {
  const { usuario, puede, puedeEn } = usarUsuario();
  const inicial = useMemo(() => (editarId ? null : leerBorrador(usuario.id)), [editarId, usuario.id]);
  const [forma, setForma] = useState<FormaCuestionario>(inicial?.forma ?? formaVacia());
  const [paso, setPaso] = useState<Paso>(inicial?.paso ?? 'inicio');
  const [errores, setErrores] = useState<ErroresForma>({});
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [original, setOriginal] = useState<Cotizacion | null>(null);
  const [cargando, setCargando] = useState(Boolean(editarId));
  const [vendedores, setVendedores] = useState<Usuario[]>([]);
  const [vendedorId, setVendedorId] = useState<string>('');
  // punto o (Diego, 13-oct, 19:05): vendedores sin tarjeta en la plantilla y sin WhatsApp (su cotización no saca PDF)
  const [sinWhatsapp, setSinWhatsapp] = useState<string[]>([]);
  // MULTISITIO (Diego, 13-oct): null = un solo sitio; si no, los nombres de las sucursales.
  const [sitios, setSitios] = useState<SitiosForma>(null);
  const [errorSitios, setErrorSitios] = useState<string | null>(null);
  const cabeza = useRef<HTMLHeadingElement>(null);
  // ADENDA z52-2: cada salida del campo «¿Por qué?» le pide al aumento con IA que calcule (si hace falta)
  const [pedidoAumento, setPedidoAumento] = useState(0);
  // «Calcular el aumento» de un aviso abre el cuestionario en ese punto (?campo=aumento)
  const { consulta } = usarRuta();
  const campoPedido = consulta.get('campo');

  useEffect(() => {
    if (!editarId) return;
    api
      .detalle(editarId)
      .then((d) => {
        setOriginal(d.cotizacion);
        // la tarifa leída del recibo decide si aplica la pregunta del transformador cuando el cuestionario dice "No sé"
        // bloqueo del verificador (28-oct): con la tarifa leída y el parámetro negocio_a_pdbt de ESTA cotización se decide, como en
        // el motor, si «va a subir» ofrece el aumento con IA o el consumo futuro de antes (GDBT, GDMTO y GDMTH)
        setForma(formaDesde(d.cotizacion.borrador.cuestionario, { tarifaLeida: d.cotizacion.borrador.lecturas.A?.tarifa ?? null, negocioAPdbt: d.cotizacion.borrador.parametros.negocio_a_pdbt === true }));
        if (campoPedido === 'aumento') {
          setPaso('situacion');
          window.setTimeout(() => document.querySelector<HTMLElement>('[data-campo="aumento"]')?.scrollIntoView({ block: 'center' }), 0);
        }
      })
      .catch(setError)
      .finally(() => setCargando(false));
    // (el campo pedido solo cuenta al abrir)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editarId]);

  // Junta, segunda tanda (13-oct): Diego, Miguel y Juan Carlos eligen a nombre de qué vendedor sale (un externo, solo él).
  useEffect(() => {
    if (usuario.rol === 'externo' || editarId) return;
    api
      .usuarios()
      .then((r) => {
        setVendedores(r.usuarios);
        setSinWhatsapp(r.sin_whatsapp_ni_tarjeta ?? []);
      })
      .catch(() => setVendedores([]));
  }, [usuario.rol, editarId]);
  const eligeVendedor = cotizaANombreDeOtros(vendedores, usuario.id);

  useEffect(() => {
    if (!editarId) guardarBorradorLocal(usuario.id, forma, paso);
  }, [forma, paso, editarId, usuario.id]);

  const poner = <K extends keyof FormaCuestionario>(k: K, v: FormaCuestionario[K]) => {
    setForma((f) => ({ ...f, [k]: v }));
    if (errores[k as string]) setErrores((e) => ({ ...e, [k as string]: undefined }));
  };
  const err = (k: string) => errores[k] ?? null;
  const indice = PASOS.indexOf(paso);
  // Ampliación de baterías (Diego, 27-oct-2026): otro tipo de cotización (sin recibo, tarifa, consumo ni régimen). En el alta
  // pide además la batería y cuántas tiene y suma (con eso nace contenido.piezas); al editar, solo el cuestionario.
  const amp = esAmpliacionForma(forma);
  const conPiezas = amp && !original;
  const [bateriasAmp, setBateriasAmp] = useState<BateriaVista[] | null>(null);
  const [errorBaterias, setErrorBaterias] = useState<unknown>(null);
  useEffect(() => {
    if (!conPiezas || bateriasAmp) return;
    let vivo = true;
    pedirVistaPrecios()
      .then((v) => vivo && setBateriasAmp(v.baterias_ampliacion ?? []))
      .catch((e: unknown) => vivo && setErrorBaterias(e));
    return () => {
      vivo = false;
    };
  }, [conPiezas, bateriasAmp]);
  const bateriaAmp = bateriasAmp?.find((b) => claveBateria(b) === forma.ampBateria) ?? null;
  /** La ficha manda: más baterías de las que permite en paralelo no se crean (el servidor lo marcaría en rojo). */
  const problemaAmp = (): Record<string, string> => {
    if (!conPiezas) return {};
    if (!bateriaAmp) return forma.ampBateria ? { ampBateria: 'Esa batería ya no está en la lista: elige otra.' } : {};
    const r = rangoAmpliacion(bateriaAmp, numeroDe(forma.ampActuales), numeroDe(forma.ampNuevas));
    return r?.pasa ? { ampNuevas: `Con ${forma.ampActuales.trim()} que ya tiene y ${forma.ampNuevas.trim()} nuevas son ${r.total}: la ficha de la ${bateriaAmp.marca} ${bateriaAmp.modelo} permite ${r.maximo} en paralelo.` } : {};
  };
  const recibos = recibosPorMedidor(forma);
  const negocio = forma.tipo === 'comercio' || forma.tipo === 'industria';
  // Baterías o aislado de un negocio: el giro se puede escribir (opcional); el régimen es obligatorio (20-oct-2026: decide el
  // producto de FINSUS; sin él la empresa salía con el de persona).
  const clienteNegocio = forma.clienteTipo === 'comercio' || forma.clienteTipo === 'industria';
  const pideRegimen = negocioDeForma(forma);
  // La tarifa que va a tener un cliente nuevo sale del TIPO DE CLIENTE elegido al inicio, también en baterías (verificación del
  // 14-oct: Comercio + «Solo baterías de respaldo» + «Cliente nuevo» no la enseñaba y el PDF salía como casa, «tu casa» y 2–3
  // semanas). El aislado no tiene CFE: no lleva tarifa.
  const tarifaDeNegocio = negocio || (forma.tipo === 'baterias' && clienteNegocio);
  const tarifaDeIndustria = forma.tipo === 'industria' || (forma.tipo === 'baterias' && forma.clienteTipo === 'industria');

  function irA(p: Paso) {
    setPaso(p);
    window.scrollTo({ top: 0 });
    window.setTimeout(() => cabeza.current?.focus(), 0);
  }

  function siguiente(f: FormaCuestionario = forma) {
    if (!original && paso === 'inicio' && problemaSitios(sitios)) {
      setErrorSitios(problemaSitios(sitios));
      return;
    }
    const r = validar(f, paso, !original);
    const extra = paso === 'situacion' ? problemaAmp() : {};
    if (Object.keys(r.errores).length || Object.keys(extra).length) {
      setErrores({ ...extra, ...r.errores });
      // Verificación del 27-oct: con el ÚNICO error de la ficha (más baterías de las que permite en paralelo) no hay error del
      // cuestionario y pasoDeCampo(undefined) tronaba (TypeError). El error de la ficha es de ESTE paso: aquí se queda.
      const primero = Object.keys(r.errores)[0] ?? Object.keys(extra)[0];
      const p = primero !== undefined ? pasoDeCampo(primero) : paso;
      if (p !== paso) irA(p);
      return;
    }
    setErrores({});
    if (paso === 'inicio') setForma(omisionesClienteNuevo(cerrarInicio(f)));
    if (paso !== 'notas') irA(PASOS[indice + 1]!);
    else void terminar();
  }

  /** z58: «Usar estos consumos»: autoriza la tabla de la IA (como está ahora) y sigue al siguiente paso. Si falta otra cosa del
   *  paso, la validación lo dice ahí mismo. */
  function usarConsumos() {
    const f = { ...forma, consumosAutorizados: firmaConsumos(forma.tipo, forma.cargas) };
    setForma(f);
    siguiente(f);
  }

  async function terminar() {
    const r = validar(forma, 'notas', !original);
    const extra = problemaAmp();
    if (!r.cuestionario || Object.keys(extra).length) {
      setErrores({ ...extra, ...r.errores });
      if (Object.keys(extra).length) irA('situacion');
      return;
    }
    setEnviando(true);
    setError(null);
    try {
      if (original) {
        const resp = await api.editarBorrador(original.id, original.revision, [
          { op: 'reemplazar', ruta: '/cuestionario', antes: original.borrador.cuestionario, despues: r.cuestionario, motivo: 'Cuestionario editado' },
        ]);
        navegar(`/cotizaciones/${resp.cotizacion.id}`);
      } else if (r.cuestionario.tipo === 'ampliacion_baterias') {
        // Ampliación de baterías: nace con la batería (de la tabla con su costo de tabla; del catálogo, sin costo: el vendedor
        // teclea el del proveedor en su pantalla) y las cantidades; lo demás se captura en «Ampliación».
        if (!bateriaAmp) {
          setErrores({ ampBateria: 'Elige la batería que ya tiene.' });
          irA('situacion');
          return;
        }
        const piezas = {
          ...piezasAmpliacionVacia(bateriaDeVista(bateriaAmp)),
          actuales: numeroDe(forma.ampActuales),
          nuevas: numeroDe(forma.ampNuevas),
          costo_bateria: bateriaAmp.costo_en_tabla ? { origen: 'tabla' as const } : null,
        };
        const resp = await api.crearCotizacion(r.cuestionario, vendedorId || null, undefined, piezas);
        borrarBorradorLocal(usuario.id);
        // z48 (Diego, 28-oct): reemplaza «Nueva» en el historial: «Atrás» ya no cae en un cuestionario vacío (el borrador local
        // se borró al crear); al cuestionario se regresa con «Editar cuestionario» del encabezado.
        navegar(`/cotizaciones/${resp.cotizacion.id}/ampliacion`, { reemplazar: true });
      } else {
        const problema = problemaSitios(sitios);
        if (problema) {
          setErrorSitios(problema);
          irA('inicio');
          return;
        }
        // z65 · candado 1: el cuestionario de CADA sucursal (su tipo y su tarifa) antes de crear nada; si una no pasa, no se crea
        // ninguna y se dice cuál.
        const armados = sitios ? cuestionariosDeSitios(forma, r.cuestionario, sitios) : null;
        if (armados && 'error' in armados) {
          setErrorSitios(armados.error);
          irA('inicio');
          return;
        }
        const lista = armados?.sitios ?? null;
        const resp = await api.crearCotizacion(r.cuestionario, vendedorId || null, lista ? { sitio: lista[0]!.nombre, junto_a: null } : undefined);
        borrarBorradorLocal(usuario.id);
        // MULTISITIO: las demás sucursales, en el mismo proyecto (si una falla, la primera ya existe y su franja deja agregarla).
        const faltaron: string[] = [];
        // z65 · candado 1: lo que guardó el servidor de cada sucursal (tipo y tarifa) contra lo que se pidió.
        const distintas: string[] = [];
        const d0 = lista ? diferenciaSitio(r.cuestionario, resp.cotizacion.borrador.cuestionario) : null;
        if (d0) distintas.push(`«${lista![0]!.nombre}»: ${d0}`);
        for (const sitio of lista?.slice(1) ?? []) {
          try {
            const otra = await api.crearCotizacion(sitio.cuestionario, vendedorId || null, { sitio: sitio.nombre, junto_a: resp.cotizacion.id });
            const d = diferenciaSitio(sitio.cuestionario, otra.cotizacion.borrador.cuestionario);
            if (d) distintas.push(`«${sitio.nombre}»: ${d}`);
          } catch {
            faltaron.push(sitio.nombre);
          }
        }
        // Verificación del 13-oct: una sucursal que no se creó se DICE (antes se perdía en silencio).
        if (faltaron.length) window.alert(`No se pudo crear ${faltaron.length === 1 ? 'la sucursal' : 'las sucursales'} ${faltaron.join(', ')}. Agrégala${faltaron.length === 1 ? '' : 's'} con «Agregar sucursal» en la franja de arriba.`);
        if (distintas.length) window.alert(`Revisa el tipo y la tarifa en «Sitios del proyecto» → «Editar»: ${distintas.join('; ')}.`);
        navegar(`/cotizaciones/${resp.cotizacion.id}/${r.cuestionario.consumo.es_nuevo ? 'opciones' : 'recibos'}`, { reemplazar: true });
      }
    } catch (e) {
      setError(e);
    } finally {
      setEnviando(false);
    }
  }

  function empezarDeNuevo() {
    borrarBorradorLocal(usuario.id);
    setForma(formaVacia());
    setSitios(null);
    setErrores({});
    irA('inicio');
  }

  /** Elegir el enfoque precarga y enfoca el resto (logica/cuestionario.ts → conEnfoque). */
  function ponerEnfoque(cambio: { clienteTipo?: ClienteTipo; queCotiza?: QueCotiza }) {
    setForma((f) => conEnfoque(f, cambio));
    // el aislado no lleva varios sitios (sin CFE, va por equipos): se quita para que no se creen sucursales escondidas; la
    // ampliación de baterías tampoco (es de un solo sistema)
    if (cambio.queCotiza === 'aislado' || cambio.queCotiza === 'ampliacion_baterias') setSitios(null);
    setErrores((e) => ({ ...e, clienteTipo: undefined, queCotiza: undefined, tipo: undefined }));
  }

  const ponerCargaRespaldo = (i: number, k: keyof FormaCargaRespaldo, v: string) => {
    setForma((f) => ({ ...f, respaldoCargas: f.respaldoCargas.map((c, j) => (j === i ? { ...c, [k]: v } : c)) }));
    const claves = [`cargaRespaldo.${i}`, `cargaRespaldo.${i}.horas_de_dia`, `cargaRespaldo.${i}.horas_noche`];
    if (claves.some((k) => errores[k])) setErrores((e) => ({ ...e, ...Object.fromEntries(claves.map((k) => [k, undefined])) }));
  };
  const aislado = forma.tipo === 'aislado';
  const kwRespaldo = kwDeCargas(forma.respaldoCargas);
  // SIM-H2 (27-oct): además de la tarjeta, lo que ya marcó (p. ej. paneles + baterías de una casa nueva).
  const sinPdf = forma.queCotiza ? (queCotizaSinPdf(forma.queCotiza) ?? pdfTodaviaNoSaleForma(forma)) : null;
  const kwhEquipos = aislado ? kwhMesDeEquiposAislado(forma.respaldoCargas) : null;

  if (!puede('editar_cotizaciones'))
    return (
      <div className="pagina">
        <ErrorEnLinea error={new Error('Tu usuario solo captura costos y fotos; no crea cotizaciones.')} />
      </div>
    );
  if (cargando) return <div className="pagina"><Cargando renglones={6} /></div>;
  // No se pudo abrir la cotización (p. ej. privacidad A0b: un externo no abre lo ajeno): nada de formulario vacío.
  if (editarId && !original)
    return (
      <div className="pagina">
        <ErrorEnLinea error={error ?? new Error('No se pudo abrir esta cotización.')} />
      </div>
    );
  // Política "vendedor externo edita solo lo suyo" (contrato v2): editar el cuestionario de otro no se ofrece (el
  // servidor respondería 403 al guardar).
  if (original && !puedeEn('editar_cotizaciones', original))
    return (
      <div className="pagina">
        <ErrorEnLinea error={new Error('Esta cotización no es tuya: su cuestionario solo lo edita su vendedor o Diego.')} />
      </div>
    );

  // Diego, 12-oct tarde (ADENDA, MANDA): en media tensión (GDMTO/GDMTH) la tensión del transformador es PREGUNTA OBLIGATORIA,
  // justo debajo de la tarifa: 220 o 440 V (Diego, 24-oct), o «No sé: lo veo en la visita» (= trifásico sin tensión: el
  // cotizador calcula con 220 V y deja un aviso amarillo). 480 V solo aparece si el cuestionario ya lo traía.
  // Verificación adversarial de la etapa 2: (1) la respuesta es su PROPIO campo (forma.transformador): un «Trifásico» tocado en la
  // pregunta de baja tensión ya no la deja contestada con «No sé»; (2) también sale con la tarifa en "No sé" cuando el
  // cotizador la calcula en media tensión (industria nueva, o la tarifa leída del recibo al editar): mediaTensionForma.
  const mediaTension = mediaTensionForma(forma);
  // Diego, 24-oct: en baja tensión 440 V solo con transformador propio: ni en una casa ni con tarifa PDBT o GDBT (la del
  // cuestionario o, con «No sé», la leída del recibo al editar); sí con la tarifa en «No sé» en un negocio o industria.
  const tarifaBaja = forma.tarifa !== 'no_se' ? forma.tarifa : forma.tarifaLeida;
  const ofrece440 = forma.tipo !== 'casa' && tarifaBaja === null;
  const transformadorDeAntes = forma.transformador && forma.transformador !== 'visita' && !TENSIONES_TRANSFORMADOR.includes(forma.transformador) ? [forma.transformador] : [];
  const preguntaTransformador = mediaTension ? (
    <Segmentado<TensionServicio | 'visita'>
      etiqueta="Tensión del transformador (obligatoria en media tensión)"
      valor={forma.transformador}
      alCambiar={(v) => {
        poner('transformador', v);
        if (errores['servicioTension']) setErrores((e) => ({ ...e, servicioTension: undefined }));
      }}
      opciones={[
        ...[...TENSIONES_TRANSFORMADOR, ...transformadorDeAntes].map((t) => ({
          valor: t,
          texto: `${t} V`,
          detalle: t === '220' ? 'Entre fases (220Y/127 V)' : t === '480' ? 'Entre fases (480Y/277 V)' : t === '440' ? 'Entre fases (440 V)' : 'Entre fases',
        })),
        { valor: 'visita' as const, texto: ETIQUETA_NO_SE_TRANSFORMADOR, detalle: 'Se calcula con 220 V y queda un aviso amarillo' },
      ]}
      columnas={2}
      ayuda={
        forma.queCotiza === 'peak_shaving'
          ? 'Es la del lado de baja del transformador de la planta (la placa del transformador o el tablero principal). El gabinete es de 480 V: con 440 o 480 V va directo; con 220 V (o «No sé») lleva transformador elevador, que sube el precio.'
          : 'Es la del lado de baja del transformador de la planta (la placa del transformador o el tablero principal). Decide qué inversores Growatt se conectan.'
      }
      error={err('servicioTension')}
    />
  ) : null;

  // Casa o negocio: en baterías lo dice el tipo de cliente elegido al inicio (verificación del 14-oct: una casa con baterías solo
  // veía «No sé» y tarifas de negocio, sin 1F ni DAC). Una tarifa que ya traía y no es de su lista se queda visible.
  const paraTarifa = llevaRespaldo(forma.tipo) ? forma.clienteTipo : forma.tipo;
  // ADENDA z52-2 + bloqueo del verificador (29-oct): cómo se ofrece «va a subir» ('ia' | 'supuesta' | 'produccion')
  const modoAumento = modoAumentoForma(forma);
  // z65: la MISMA lista de tarifas que cada sucursal y «Separar en otro sitio» (logica/sitios.ts).
  const tarifasOpciones = opcionesTarifa(paraTarifa, forma.tarifa);

  return (
    <div className="pagina pagina--formulario">
      <header className="asistente__cabeza">
        <p className="asistente__contexto">{original ? `Editar cuestionario · ${original.folio}` : 'Nueva cotización'}</p>
        <h1 className="pagina__titulo" ref={cabeza} tabIndex={-1}>
          {(amp ? TITULO_PASO_AMPLIACION : TITULO_PASO)[paso]}
        </h1>
        <ol className="pasos" aria-label="Pasos">
          {PASOS.map((p, i) => (
            <li key={p} className={['pasos__paso', i < indice ? 'es-hecho' : '', i === indice ? 'es-actual' : ''].join(' ')}>
              <button type="button" disabled={!original && i > indice} onClick={() => irA(p)} aria-current={i === indice ? 'step' : undefined}>
                <span className="pasos__numero">{i < indice ? <Check aria-hidden /> : i + 1}</span>
                <span className="pasos__nombre">{(amp ? TITULO_PASO_AMPLIACION : TITULO_PASO)[p]}</span>
              </button>
            </li>
          ))}
        </ol>
      </header>

      <div className="hoja asistente__hoja">
        {paso === 'cliente' && (
          <div className="pila">
            <CampoTexto etiqueta="Nombre del cliente" ayuda="Así sale en el PDF (máximo 60 letras)." maxLength={60} autoComplete="off" value={forma.nombre} onChange={(e) => poner('nombre', e.target.value)} error={err('nombre')} />
            {(negocio || clienteNegocio) && !amp && (
              <CampoTexto
                etiqueta="Giro del negocio"
                placeholder="Ej. abarrotes, taller, empacadora"
                // Hallazgo del 10-oct: en la portada comercial caben 20 letras; más largo, el PDF salía sin giro y nadie se enteraba.
                maxLength={20}
                // baterías o aislado: la plantilla de baterías no imprime el giro (verificación del 14-oct)
                ayuda={negocio ? `Sale en la portada: caben 20 letras (${forma.giro.length} de 20).` : `Opcional: el PDF de baterías no lo imprime; queda en la cotización (${forma.giro.length} de 20).`}
                value={forma.giro}
                onChange={(e) => poner('giro', e.target.value)}
                error={err('giro')}
              />
            )}
            {pideRegimen && (
              // Diego, 12-oct: persona moral o física con actividad empresarial deducen el 100 % (LISR art. 34, fr. XIII): la
              // propuesta muestra el beneficio fiscal estimado y un segundo retorno con la deducción. Particular: nada de esto.
              // Revisión de ventas, propuesta 5 (12-oct noche): OBLIGATORIO en comercio e industria; «No sé todavía» es respuesta.
              <CampoSelect
                etiqueta="Régimen fiscal del cliente"
                value={forma.regimen ?? ''}
                onChange={(e) => poner('regimen', (e.target.value || null) as typeof forma.regimen)}
                opciones={[{ valor: '', texto: 'Elige el régimen…' }, ...REGIMENES_FISCALES.map((r) => ({ valor: r, texto: ETIQUETA_REGIMEN_FISCAL[r] }))]}
                ayuda={
                  negocio
                    ? 'Obligatorio. Con persona moral o actividad empresarial, la propuesta dice el beneficio fiscal estimado y en cuánto se paga con la deducción, y el financiamiento va con el Crédito Solar Empresarial de FINSUS. La persona física en RESICO no deduce (va como particular). Si el cliente no lo sabe, «No sé todavía»: la deducción va sin cifras.'
                    : 'Obligatorio. Con persona moral o actividad empresarial, el financiamiento va con el Crédito Solar Empresarial de FINSUS; particular, RESICO o «No sé todavía», con el de persona.'
                }
                error={err('regimen')}
              />
            )}
            {pideRegimen && forma.regimen === 'pf_empresarial' && (
              <CampoTexto
                etiqueta="Su tasa de ISR"
                inputMode="decimal"
                sufijo="%"
                value={forma.tasaIsr}
                onChange={(e) => poner('tasaIsr', e.target.value)}
                ayuda="Por omisión 30 %; la de su declaración si la sabe (persona moral: 30 %)."
                error={err('tasaIsr')}
              />
            )}
            <div className="rejilla-2">
              <CampoTexto etiqueta="Teléfono (opcional)" inputMode="tel" autoComplete="off" placeholder="10 dígitos" value={forma.telefono} onChange={(e) => poner('telefono', e.target.value)} error={err('telefono')} />
              <CampoTexto etiqueta="Correo (opcional)" type="email" inputMode="email" autoComplete="off" value={forma.correo} onChange={(e) => poner('correo', e.target.value)} error={err('correo')} />
            </div>
            <div className="rejilla-2">
              <CampoTexto etiqueta="Ciudad" value={forma.ciudad} maxLength={40} onChange={(e) => poner('ciudad', e.target.value)} error={err('ciudad')} />
              <CampoTexto etiqueta="Estado" value={forma.estado} maxLength={40} onChange={(e) => poner('estado', e.target.value)} error={err('estado')} />
            </div>
            {/* aislado: no hay recibo de CFE que revisar (verificación del 14-oct) */}
            {!aislado && !amp && <SiNo
              etiqueta="¿El recibo de CFE está a nombre del cliente?"
              valor={forma.titularEsCliente}
              alCambiar={(v) => poner('titularEsCliente', v)}
              si="Sí, a su nombre"
              no="No, de otra persona"
              ayuda="Pasa seguido: el papá, el arrendador o la empresa. La IA revisa que coincida."
              error={err('titularEsCliente')}
            />}
            {!aislado && !amp && forma.titularEsCliente === false && (
              <CampoTexto etiqueta="¿A nombre de quién está?" maxLength={120} value={forma.titularNombre} onChange={(e) => poner('titularNombre', e.target.value)} error={err('titularNombre')} ayuda="No sale en el PDF; solo sirve para revisar el recibo." />
            )}
            {eligeVendedor && !original && (
              <SelectorVendedor usuarios={vendedores} yoId={usuario.id} valor={vendedorId || usuario.id} alCambiar={(id) => setVendedorId(id === usuario.id ? '' : id)} />
            )}
          </div>
        )}

        {paso === 'inicio' && (
          <div className="pila">
            <Segmentado<ClienteTipo>
              etiqueta="Tipo de cliente"
              valor={forma.clienteTipo}
              alCambiar={(v) => ponerEnfoque({ clienteTipo: v })}
              opciones={(['casa', 'comercio', 'industria'] as const).map((t) => ({ valor: t, texto: ETIQUETA_CLIENTE[t], detalle: DETALLE_CLIENTE[t], icono: ICONO_CLIENTE[t] }))}
              columnas={3}
              grande
              error={err('clienteTipo')}
            />
            <Segmentado<QueCotiza>
              etiqueta="¿Qué se cotiza?"
              valor={forma.queCotiza}
              alCambiar={(v) => ponerEnfoque({ queCotiza: v })}
              // Al editar, una ampliación de baterías no se vuelve solar ni al revés (su captura vive en otro lado: contenido.piezas)
              opciones={queCotizaOpciones(forma.clienteTipo, forma.queCotiza).filter((q) => !original || (original.borrador.cuestionario.tipo === 'ampliacion_baterias') === (q === 'ampliacion_baterias')).map((q) => ({ valor: q, texto: ETIQUETA_QUE[q], detalle: detalleQue(q), icono: ICONO_QUE[q], deshabilitada: !QUE_COTIZA_DISPONIBLE[q] }))}
              columnas={2}
              grande
              error={err('queCotiza') ?? err('tipo')}
            />
            {sinPdf && (
              <div className="aviso-ancho aviso-ancho--ambar" role="note">
                <strong>{ETIQUETA_QUE[forma.queCotiza!]}: el PDF todavía no sale.</strong> {sinPdf} El cotizador sí calcula las opciones.
              </div>
            )}
            {/* Recorte de punta (ADENDA z54-1, 29-oct-2026): solo industria que trabaja de noche y no puede (o ya tiene) paneles */}
            {forma.queCotiza === 'peak_shaving' && (
              <div className="pila" aria-label="Recorte de punta">
                <SiNo
                  etiqueta="¿Trabaja entre semana de 6 a 10 de la noche? (24 h, o 2 o 3 turnos)"
                  valor={forma.recorteNoche}
                  alCambiar={(v) => poner('recorteNoche', v)}
                  si="Sí"
                  no="No"
                  error={err('recorteNoche')}
                />
                <Segmentado<'sin_techo' | 'ya_tiene' | 'le_caben'>
                  etiqueta="¿Por qué sin paneles nuevos?"
                  valor={forma.recorteSinPaneles}
                  alCambiar={(v) => poner('recorteSinPaneles', v)}
                  opciones={[
                    { valor: 'sin_techo', texto: 'No tiene techo ni terreno' },
                    // verificación de hoy70: «Ya tiene paneles» dejaba pasar a quien tiene paneles y le caben más (la ampliación va primero)
                    { valor: 'ya_tiene', texto: 'Ya tiene paneles y no le caben más' },
                    { valor: 'le_caben', texto: 'Le caben paneles (o más paneles)' },
                  ]}
                  columnas={3}
                  error={err('recorteSinPaneles')}
                  ayuda="En GDMTH van primero los paneles: bajan la capacidad con la fórmula de CFE. La batería es para quien ya no puede (o ya los tiene)."
                />
                {(forma.recorteNoche === false || forma.recorteSinPaneles === 'le_caben') && (
                  <div className="aviso-ancho aviso-ancho--ambar" role="alert">
                    <strong>No va el recorte de punta.</strong> {forma.recorteNoche === false ? MENSAJE_RECORTE_SIN_NOCHE : MENSAJE_RECORTE_LE_CABEN}
                  </div>
                )}
              </div>
            )}
            {forma.queCotiza && forma.clienteTipo && !amp && forma.queCotiza !== 'peak_shaving' && (
              <fieldset className="segmentado">
                <legend className="campo__etiqueta">Casos especiales (marca los que apliquen)</legend>
                <div className="casos-especiales">
                  {!original && !aislado && (
                    <Interruptor
                      etiqueta="Varios sitios / sucursales"
                      detalle="Una cotización por sucursal, todas del mismo proyecto"
                      marcado={sitios !== null}
                      alCambiar={(v) => {
                        setSitios(v ? (sitios ?? [sitioVacio(), sitioVacio()]) : null);
                        setErrorSitios(null);
                      }}
                    />
                  )}
                  {!aislado && !forma.esNuevo && (
                    <Interruptor etiqueta="Dos recibos / dos medidores" detalle="Se suben los de cada medidor y se suman mes a mes" marcado={forma.dosMedidores === true} alCambiar={(v) => poner('dosMedidores', v)} />
                  )}
                  {enfoqueConPaneles(forma.queCotiza) && (
                    <Interruptor etiqueta="Ampliación (ya tiene paneles)" detalle="Se pide lo que ya tiene instalado" marcado={forma.ampliacion === true} alCambiar={(v) => poner('ampliacion', v)} />
                  )}
                  {/* z10: industria nueva ya no se ofrece (cliente nuevo: casa, lo más común, o negocio) */}
                  {!aislado && (!tarifaDeIndustria || forma.esNuevo === true) && (
                    <Interruptor
                      etiqueta="Cliente nuevo: sin recibos de CFE"
                      detalle="Casa o negocio nuevo: se estima su consumo"
                      marcado={forma.esNuevo === true}
                      alCambiar={(v) => setForma((f) => ({ ...f, esNuevo: v, ...(v ? { dosMedidores: false, ...(f.kwhMes.trim() ? {} : { metodoEstimacion: 'cuadro_cargas' as const }) } : {}) }))}
                    />
                  )}
                  <Interruptor
                    etiqueta="Va a subir el consumo"
                    detalle="Auto eléctrico, otro cuarto, más equipos"
                    marcado={forma.cambio === 'aumenta'}
                    alCambiar={(v) => poner('cambio', v ? 'aumenta' : 'igual')}
                  />
                </div>
                {aislado && <p className="campo__ayuda">Aislado: no hay recibo de CFE; el consumo se estima en el paso «Situación».</p>}
              </fieldset>
            )}
            {sitios && !original && (
              <NombresSitios
                principal={{ tipo: forma.clienteTipo, tarifa: forma.tarifa }}
                sitios={sitios}
                alCambiar={(x) => {
                  setSitios(x);
                  setErrorSitios(null);
                }}
                error={errorSitios}
              />
            )}
            {amp && (
              <p className="nota-suave">
                Formato del PDF: <strong>{ETIQUETA_FORMATO.piezas}</strong>: una hoja con lo que incluye (baterías, material e instalación) y un solo precio; con una hoja 2 opcional que explica, con su monitoreo, cuánto le ayudan las baterías nuevas.
              </p>
            )}
            {forma.tipo && !amp && (
              <p className="nota-suave">
                {/* el recorte de punta va en el formato horaria (verificación de hoy70: decía «industrial» porque no le pasaba el recorte) */}
                Formato del PDF: <strong>{ETIQUETA_FORMATO[formatoPara({ tipo: forma.tipo, tarifa: forma.tarifa, recorte_punta: forma.queCotiza === 'peak_shaving' ? { trabaja_de_noche: true, sin_paneles: forma.recorteSinPaneles === 'ya_tiene' ? 'ya_tiene' : 'sin_techo' } : null })]}</strong>
                {forma.queCotiza === 'peak_shaving'
                  ? ' (la batería sola, con sus 12 recibos GDMTH: 5 hojas y su anexo).'
                  : forma.tipo === 'aislado' ? ' (el aislado va en el formato de baterías).' : forma.tipo === 'comercio' || forma.tipo === 'industria' ? ' (lo decide la tarifa del recibo: GDMTH va en industrial).' : '.'}
              </p>
            )}
          </div>
        )}

        {paso === 'situacion' && amp && (
          <div className="pila">
            <SiNo
              etiqueta="¿Ya se hizo la visita técnica?"
              valor={forma.visita}
              alCambiar={(v) => poner('visita', v)}
              si="Sí: cotización final"
              no="No: precotización"
              ayuda={forma.visita === false ? 'La precotización avisa al cliente lo que puede cambiar con la visita (espacio en el gabinete, cableado).' : undefined}
              error={err('visita')}
            />
            {conPiezas ? (
              <>
                {errorBaterias ? <ErrorEnLinea error={errorBaterias} /> : null}
                <CampoSelect
                  etiqueta="Batería que ya tiene"
                  value={forma.ampBateria}
                  onChange={(e) => poner('ampBateria', e.target.value)}
                  disabled={bateriasAmp === null}
                  opciones={[
                    { valor: '', texto: bateriasAmp === null ? 'Cargando las baterías…' : 'Elige la batería…' },
                    ...(bateriasAmp ?? []).map((b) => ({ valor: claveBateria(b), texto: textoBateria(b) })),
                  ]}
                  ayuda="Las nuevas son del mismo modelo (en paralelo con las que ya tiene)."
                  error={err('ampBateria')}
                />
                <div className="rejilla-2">
                  <CampoTexto etiqueta="Cuántas tiene" inputMode="numeric" value={forma.ampActuales} onChange={(e) => poner('ampActuales', e.target.value)} error={err('ampActuales')} />
                  <CampoTexto etiqueta="Cuántas suma" inputMode="numeric" value={forma.ampNuevas} onChange={(e) => poner('ampNuevas', e.target.value)} error={err('ampNuevas')} />
                </div>
                {(() => {
                  const r = rangoAmpliacion(bateriaAmp, numeroDe(forma.ampActuales), numeroDe(forma.ampNuevas));
                  if (!r || !bateriaAmp) return null;
                  return (
                    <p className={r.pasa ? 'texto-rojo' : 'nota-suave'} role="status">
                      De <strong>{kwhCorto(r.hoy)}</strong> a <strong>{kwhCorto(r.con)} kWh</strong> · {r.total} de {r.maximo} baterías en paralelo
                      {r.pasa ? ': pasa del máximo de la ficha.' : '.'}
                    </p>
                  );
                })()}
                {bateriaAmp && !bateriaAmp.costo_en_tabla && (
                  <p className="nota-suave">Esta batería no trae costo en la tabla de precios: en la cotización tecleas el costo del proveedor con su cotización.</p>
                )}
                <p className="nota-suave">Los costos (material, instalación, viáticos), el anticipo y la hoja 2 con la noche medida se capturan en la cotización, en «Ampliación».</p>
              </>
            ) : (
              <p className="nota-suave">La batería, cuántas suma y los costos se cambian en la cotización, en «Ampliación».</p>
            )}
          </div>
        )}

        {paso === 'situacion' && !amp && (
          <div className="pila">
            {llevaRespaldo(forma.tipo) && (
              <div className="grupo-respaldo pila">
                <p className="grupo-respaldo__titulo">{aislado ? 'Equipos que van a funcionar con las baterías' : 'Respaldo con baterías'}</p>
                {/* Respaldo o híbrido ya se eligió en «Qué cotizas» (solo baterías · paneles + baterías). */}
                {!aislado && !forma.queCotiza && (
                  <Segmentado<FormaCuestionario['respaldoTipo']>
                    etiqueta="Tipo de sistema"
                    valor={forma.respaldoTipo}
                    alCambiar={(v) => poner('respaldoTipo', v)}
                    opciones={[
                      { valor: 'respaldo', texto: 'Respaldo', detalle: 'Baterías para los apagones' },
                      { valor: 'hibrido', texto: 'Híbrido', detalle: 'Paneles, baterías y CFE juntos' },
                    ]}
                    columnas={2}
                  />
                )}
                {!aislado && (
                  <p className="nota-suave">
                    Se respalda <strong>toda la casa</strong>: el sistema va al centro de carga principal (sin separar circuitos). El cotizador
                    {forma.queCotiza === 'paneles_baterias'
                      ? 'arranca cada opción con baterías para 1 a 2 horas (apagones comunes); en Opciones se ajustan las horas, las baterías y el inversor.'
                      : 'calcula las horas de respaldo (unas 2, 4 y 8 h) con el consumo del recibo.'}
                  </p>
                )}
                {aislado && (
                <div className="cargas">
                  <p className="campo__etiqueta">Equipos</p>
                  {forma.respaldoCargas.length === 0 && (
                    <p className="nota-suave">Agrega cada equipo: cuántos, cuántos watts y cuántas horas se usa de día y de noche. Con ellos se arman los niveles Esencial, Confort y Casa completa; las baterías se calculan para la noche.</p>
                  )}
                  {forma.respaldoCargas.length > 0 && (
                    <p className="nota-suave">Horas vacías = las típicas del equipo (en gris). Cámbialas si el cliente lo usa distinto: el banco cubre lo que se usa de noche.</p>
                  )}
                  {forma.respaldoCargas.map((c, i) => {
                    // Diego, 13-oct 23:00 (respuesta w) y 28-oct (Miguel: 1 h de día y 2 de noche se rechazaba): horas de DÍA y de
                    // NOCHE por separado; vacías = las típicas que usa el motor (en gris)
                    const gris = horasEnGris(c);
                    return (
                    <div className="carga" key={i}>
                      <CampoTexto etiqueta="Equipo" value={c.equipo} placeholder="Ej. refrigerador" maxLength={60} onChange={(e) => ponerCargaRespaldo(i, 'equipo', e.target.value)} error={err(`cargaRespaldo.${i}`)} className="carga__equipo" />
                      <CampoTexto etiqueta="Cuántos" inputMode="numeric" value={c.cantidad} onChange={(e) => ponerCargaRespaldo(i, 'cantidad', e.target.value)} />
                      <CampoTexto etiqueta="Watts" inputMode="decimal" value={c.w} onChange={(e) => ponerCargaRespaldo(i, 'w', e.target.value)} />
                      <CampoTexto etiqueta="Horas de día" inputMode="decimal" value={c.horas_de_dia ?? ''} placeholder={gris.dia} onChange={(e) => ponerCargaRespaldo(i, 'horas_de_dia', e.target.value)} error={err(`cargaRespaldo.${i}.horas_de_dia`)} />
                      <CampoTexto etiqueta="Horas de noche" inputMode="decimal" value={c.horas_noche ?? ''} placeholder={gris.noche} onChange={(e) => ponerCargaRespaldo(i, 'horas_noche', e.target.value)} error={err(`cargaRespaldo.${i}.horas_noche`)} />
                      <button
                        type="button"
                        className="icono-boton carga__quitar"
                        aria-label={`Quitar ${c.equipo || 'equipo'} del respaldo`}
                        onClick={() => setForma((f) => ({ ...f, respaldoCargas: f.respaldoCargas.filter((_, j) => j !== i) }))}
                      >
                        <Trash2 aria-hidden />
                      </button>
                    </div>
                    );
                  })}
                  <div className="fila-botones fila-botones--entre">
                    <Boton chico icono={<Plus aria-hidden />} onClick={() => setForma((f) => ({ ...f, respaldoCargas: [...f.respaldoCargas, cargaRespaldoVacia()] }))}>
                      Agregar equipo
                    </Boton>
                    {kwRespaldo !== null && <span className="nota-suave">≈ {numero(kwRespaldo, kwRespaldo < 10 ? 2 : 1)} kW si todo está prendido</span>}
                  </div>
                </div>
                )}
                {(!aislado || forma.respaldoCargas.length > 0) && (
                  <>
                    {/* el aislado siempre lleva paneles: no se pregunta (verificación adversarial del 14-oct). En baterías se enseña
                        también si quedó sin contestar o distinta de lo que dice el enfoque (borrador o cotización de antes): una
                        respuesta que validar exige nunca se esconde. */}
                    {!aislado && (!forma.queCotiza || panelesFueraDeEnfoque(forma)) && (
                      <SiNo
                        etiqueta="¿Lleva paneles?"
                        valor={forma.respaldoConPaneles}
                        alCambiar={(v) => poner('respaldoConPaneles', v)}
                        si="Sí, con paneles"
                        no="Solo baterías"
                        error={err('respaldoConPaneles')}
                      />
                    )}
                    <CampoArea
                      etiqueta="Notas del respaldo (opcional)"
                      rows={2}
                      maxLength={500}
                      placeholder="Ej. que aguante toda la noche; tiene dos minisplits"
                      value={forma.respaldoNotas}
                      onChange={(e) => poner('respaldoNotas', e.target.value)}
                      error={err('respaldoNotas') ?? err('respaldo')}
                    />
                  </>
                )}
              </div>
            )}

            {/* «Cliente nuevo» y «Aislado» se eligen en «Qué cotizas»: aquí ya no se pregunta (solo si el enfoque falta, o si quedó
                sin contestar: una respuesta que validar exige nunca se esconde; verificación adversarial del 14-oct). */}
            {(!forma.queCotiza || forma.esNuevo === null) && <SiNo
              etiqueta="¿Ya tiene recibos de CFE?"
              valor={forma.esNuevo === null ? null : !forma.esNuevo}
              alCambiar={(v) => {
                poner('esNuevo', !v);
                // z11: casa o negocio nuevo: lo primero es describirlo (la IA estima); «kWh al mes» queda de atajo.
                if (!v && !aislado && !forma.kwhMes.trim()) poner('metodoEstimacion', 'cuadro_cargas');
                // Industria nueva: el motor la calcula como GDMTO; que la pantalla lo diga desde el principio.
                if (!v && tarifaDeIndustria && forma.tarifa === 'no_se') poner('tarifa', 'GDMTO');
              }}
              si="Sí, tiene recibos"
              no="No, es nuevo"
              ayuda={forma.esNuevo ? 'Casa o negocio nuevo: estimamos su consumo.' : undefined}
              error={err('esNuevo')}
            />}

            <Segmentado<FormaCuestionario['cambio']>
              etiqueta="¿Su consumo va a cambiar?"
              valor={forma.cambio}
              alCambiar={(v) => poner('cambio', v)}
              opciones={[
                { valor: 'igual', texto: 'Igual' },
                { valor: 'aumenta', texto: 'Va a subir' },
                { valor: 'disminuye', texto: 'Va a bajar' },
              ]}
              columnas={3}
            />
            {forma.cambio !== 'igual' && (
              <div className="grupo-condicional">
                <CampoArea
                  etiqueta="¿Por qué?"
                  rows={2}
                  maxLength={500}
                  placeholder={forma.cambio === 'aumenta' ? (aumentoEnForma(forma) ? 'Ej. pondrán 5 toneladas de aire adicionales' : 'Ej. va a comprar un auto eléctrico') : 'Ej. cierra una sucursal'}
                  value={forma.cambioDetalle}
                  onChange={(e) => poner('cambioDetalle', e.target.value)}
                  // bloqueo del verificador (29-oct): solo con la tarifa SABIDA la IA calcula sola al salir del campo
                  onBlur={() => modoAumento === 'ia' && setPedidoAumento((n) => n + 1)}
                  error={err('cambioDetalle')}
                />
                {modoAumento !== 'produccion' && forma.tipo ? (
                  // ADENDA z52-2: con recibo, la IA calcula cuánto sube (kWh y pesos); reemplaza «Consumo futuro al año».
                  // Bloqueo del verificador (29-oct; z55(4)): mientras la tarifa sea un SUPUESTO (cuestionario «No sé» y sin recibo: p. ej.
                  // un comercio al crear) el recibo puede salir GDBT/GDMTO/GDMTH, donde el aumento no se suma: el campo de producción
                  // sigue a la vista y la IA va solo con su botón; con el recibo (casa, PDBT o 1F de negocio) pasa al diseño.
                  <div data-campo="aumento">
                    {modoAumento === 'supuesta' && !forma.aumento && (
                      <CampoTexto etiqueta="Consumo futuro al año (si lo sabe)" inputMode="decimal" sufijo="kWh / año" value={forma.consumoFuturo} onChange={(e) => poner('consumoFuturo', e.target.value)} error={err('consumoFuturo')} />
                    )}
                    <AumentoConIA
                      tipo={forma.tipo}
                      giro={forma.giro.trim() || null}
                      descripcion={forma.cambioDetalle}
                      aumento={forma.aumento}
                      consumoFuturoViejo={forma.aumento || modoAumento === 'supuesta' ? null : numeroDe(forma.consumoFuturo)}
                      facturacion={forma.facturacion === 'mensual' || forma.facturacion === 'bimestral' ? forma.facturacion : null}
                      cotizacionId={original?.id ?? null}
                      pedido={pedidoAumento}
                      calcularAlAbrir={campoPedido === 'aumento' && modoAumento === 'ia'}
                      soloBoton={modoAumento === 'supuesta'}
                      alUsar={(a) => setForma((f) => ({ ...f, aumento: a, consumoFuturo: '' }))}
                      alQuitar={() => poner('aumento', null)}
                    />
                    {err('aumento') && <p className="campo__error">{err('aumento')}</p>}
                  </div>
                ) : (
                  // GDBT, GDMTO y GDMTH (el motor no suma el aumento con IA: también sube la demanda), «va a bajar», ampliación…: el
                  // campo de producción. data-campo: el amarillo «Captura el consumo futuro» del motor llega aquí (?campo=aumento).
                  <div data-campo="aumento">
                    <CampoTexto etiqueta="Consumo futuro al año (si lo sabe)" inputMode="decimal" sufijo="kWh / año" value={forma.consumoFuturo} onChange={(e) => poner('consumoFuturo', e.target.value)} error={err('consumoFuturo')} />
                  </div>
                )}
              </div>
            )}

            {(!forma.queCotiza || forma.ampliacion === null) && <SiNo etiqueta="¿Ya tiene paneles?" valor={forma.ampliacion} alCambiar={(v) => poner('ampliacion', v)} si="Sí, es ampliación" no="No" error={err('ampliacion')} />}
            {forma.ampliacion && (
              <div className="grupo-condicional">
                <p className="nota-suave">Lo que ya tiene instalado:</p>
                <div className="rejilla-3">
                  <CampoTexto etiqueta="Paneles" inputMode="numeric" value={forma.existentePaneles} onChange={(e) => poner('existentePaneles', e.target.value)} error={err('existentePaneles') ?? err('existente')} />
                  <CampoTexto etiqueta="Watts por panel" inputMode="decimal" value={forma.existentePanelW} onChange={(e) => poner('existentePanelW', e.target.value)} error={err('existentePanelW')} />
                  <CampoTexto etiqueta="kWp" inputMode="decimal" value={forma.existenteKwp} onChange={(e) => poner('existenteKwp', e.target.value)} error={err('existenteKwp')} />
                </div>
                <p className="nota-suave">Su inversor (sale en la propuesta):</p>
                <div className="rejilla-2">
                  <CampoTexto etiqueta="Marca del inversor" maxLength={40} placeholder="Ej. Growatt" value={forma.existenteInvMarca} onChange={(e) => poner('existenteInvMarca', e.target.value)} error={err('existenteInvMarca')} />
                  <CampoTexto etiqueta="Modelo del inversor (si lo sabe)" maxLength={40} value={forma.existenteInvModelo} onChange={(e) => poner('existenteInvModelo', e.target.value)} />
                </div>
                <div className="rejilla-3">
                  <CampoTexto etiqueta="kW del inversor" inputMode="decimal" sufijo="kW" value={forma.existenteInvKw} onChange={(e) => poner('existenteInvKw', e.target.value)} />
                  <CampoTexto etiqueta="Cuántos inversores" inputMode="numeric" value={forma.existenteInvCantidad} onChange={(e) => poner('existenteInvCantidad', e.target.value)} />
                  <CampoTexto etiqueta="Año en que se instaló" inputMode="numeric" maxLength={4} placeholder="Ej. 2021" value={forma.existenteAnio} onChange={(e) => poner('existenteAnio', e.target.value)} error={err('existenteAnio')} />
                </div>
                <Segmentado<'solarix' | 'otro' | 'no_se'>
                  etiqueta="¿Quién lo instaló?"
                  valor={forma.existenteInstalo ?? 'no_se'}
                  alCambiar={(v) => poner('existenteInstalo', v === 'no_se' ? null : v)}
                  opciones={[
                    { valor: 'solarix', texto: 'Solarix' },
                    { valor: 'otro', texto: 'Otra empresa' },
                    { valor: 'no_se', texto: 'No sé' },
                  ]}
                  columnas={3}
                />
                <Segmentado<FormaCuestionario['existenteConexion']>
                  etiqueta="¿Los paneles nuevos llevan su propio inversor?"
                  valor={forma.existenteConexion}
                  alCambiar={(v) => poner('existenteConexion', v)}
                  opciones={[
                    { valor: 'inversor_nuevo', texto: 'Sí, inversor nuevo' },
                    { valor: 'inversor_actual', texto: 'No, caben en el que tiene' },
                  ]}
                  columnas={2}
                />
                {forma.existenteInversor && !forma.existenteInvMarca && <p className="nota-suave">Antes se escribió: «{forma.existenteInversor}». Captura marca y kW para que salga en la propuesta.</p>}
                <CampoArea etiqueta="Notas del sistema actual" rows={2} maxLength={1000} value={forma.existenteNotas} onChange={(e) => poner('existenteNotas', e.target.value)} />
              </div>
            )}

            {forma.tipo && llevaPaneles(forma) && (
              // Diego, 12-oct: la estructura cambia el costo por panel y, para cuidar el margen, el precio del sistema
              <Segmentado<TipoEstructura>
                etiqueta="¿Sobre qué van los paneles?"
                valor={forma.estructura}
                alCambiar={(v) => poner('estructura', v)}
                // Diego, 12-oct tarde (ADENDA): suelo y carport solo en comercio e industria; en casa, losa/lámina o PTR (si una
                // casa ya venía con suelo o carport, se queda visible para poder cambiarla; el cotizador lo avisa en amarillo)
                opciones={TIPOS_ESTRUCTURA.filter((t) => !estructuraSoloNegocio(t) || forma.tipo !== 'casa' || t === forma.estructura).map((t) => ({ valor: t, texto: ETIQUETA_ESTRUCTURA[t], detalle: DETALLE_ESTRUCTURA[t] }))}
                columnas={2}
                ayuda={
                  forma.estructura === 'losa_lamina'
                    ? undefined
                    : 'Va dentro del precio del sistema, no como renglón aparte. No agregues además «estructura elevada», «sobre suelo» o «especial» del catálogo: sería cobrarla dos veces (el cotizador no lo deja).'
                }
                error={err('estructura')}
              />
            )}

            {/* Diego, 12-oct: fases y tensión del servicio de CFE (deciden qué inversores Growatt caben). En media tensión va, en
                su lugar, la pregunta obligatoria del transformador (debajo de la tarifa). */}
            {!mediaTension && !aislado && (
            <Segmentado<FasesServicio | 'no_se'>
              etiqueta="Servicio de CFE"
              valor={forma.servicioFases ?? 'no_se'}
              alCambiar={(v) => {
                const fases = v === 'no_se' ? null : v;
                poner('servicioFases', fases);
                if (!fases) poner('servicioTension', null);
                else if (fases === 'monofasico') poner('servicioTension', '127');
                else if (forma.servicioTension && !TENSIONES_POR_FASES[fases].includes(forma.servicioTension)) poner('servicioTension', null);
              }}
              opciones={[
                // Diego, 24-oct: solo bifásico o trifásico (un «monofásico» de antes se sigue enseñando mientras sea el valor)
                ...FASES_SERVICIO.filter((x) => FASES_EN_PANTALLA.includes(x) || x === forma.servicioFases).map((x) => ({ valor: x, texto: ETIQUETA_FASES[x].split(' (')[0]!, detalle: ETIQUETA_FASES[x].replace(/^[^(]*\(|\)$/g, '') })),
                { valor: 'no_se' as const, texto: 'No sé', detalle: 'El cotizador lo deduce y pide confirmarlo en la visita' },
              ]}
              columnas={2}
              ayuda="En el recibo, «NO HILOS»: 2 o 3 = bifásico, 4 = trifásico. Media tensión (GDMTO/GDMTH): trifásico, con la tensión del transformador."
              error={err('servicioFases')}
            />
            )}
            {!mediaTension && !aislado && forma.servicioFases && forma.servicioFases !== 'monofasico' && (
              <CampoSelect
                etiqueta="Tensión"
                value={forma.servicioTension ?? ''}
                onChange={(e) => poner('servicioTension', (e.target.value || null) as TensionServicio | null)}
                // Hallazgo del 12-oct: 440/480 V solo existen con transformador propio (media tensión); una casa no los ofrece
                // (si ya venía así de antes, se queda visible para poder cambiarlo; el cotizador lo avisa en amarillo)
                opciones={[
                  { valor: '', texto: 'No sé (se toma 220 V y se avisa)' },
                  ...TENSIONES_POR_FASES[forma.servicioFases]
                    // Diego, 24-oct: 220 o 440 V (una tensión de antes se sigue enseñando mientras sea el valor)
                    .filter((t) => TENSIONES_EN_PANTALLA.includes(t) || t === forma.servicioTension)
                    // 440 V solo con transformador propio (ofrece440); si ya venía guardado, se enseña para poder cambiarlo
                    .filter((t) => !tensionDeTransformador(t) || t === forma.servicioTension || ofrece440)
                    .map((t) => ({ valor: t, texto: ETIQUETA_TENSION[t] })),
                ]}
                ayuda={
                  forma.servicioFases !== 'trifasico'
                    ? undefined
                    : forma.tipo === 'casa'
                      ? 'Una casa trifásica es de 220 V entre fases (127 V a neutro).'
                      : ofrece440 || !tarifaBaja
                        ? 'En baja tensión (PDBT, GDBT), 220 V; 440 V solo si la planta tiene transformador propio.'
                        : `Con tarifa ${tarifaBaja}, CFE entrega 220 V entre fases (440 V solo con transformador propio, en media tensión).`
                }
                error={err('servicioTension')}
              />
            )}
            {!mediaTension && !aislado && forma.servicioFases === 'monofasico' && (
              // Diego, 24-oct: «todos son bifásicos, se instala igual, sin cambio de servicio ni costo para el cliente»
              <div className="aviso-ancho aviso-ancho--info" role="note">
                <strong>Se instala como bifásico</strong> (los inversores van a 220 V entre dos fases), sin cambio de servicio ni costo para el cliente. Puedes cambiarlo a «Bifásico».
              </div>
            )}

            {forma.esNuevo === false && (!forma.queCotiza || forma.dosMedidores === null) && (
              <SiNo
                etiqueta="¿Cuántos medidores tiene?"
                valor={forma.dosMedidores === null ? null : !forma.dosMedidores}
                alCambiar={(v) => poner('dosMedidores', !v)}
                si="Uno"
                no="Dos medidores"
                ayuda={forma.dosMedidores ? 'Se suben los recibos de cada medidor por separado y se suman mes a mes.' : undefined}
                error={err('dosMedidores')}
              />
            )}

            <SiNo
              etiqueta="¿Ya se hizo la visita técnica?"
              valor={forma.visita}
              alCambiar={(v) => poner('visita', v)}
              si="Sí: cotización final"
              no="No: precotización"
              ayuda={forma.visita === false ? 'La precotización avisa al cliente lo que puede cambiar con la visita (mufa, estructura, espacio).' : undefined}
              error={err('visita')}
            />

            {/* Auditoría del 26-oct (hallazgo 13): también al cliente nuevo (sin respuesta, la casa se cotiza bimestral, como cobra CFE). */}
            {/* recorte de punta: GDMTH mensual ya viene fijo (conEnfoque); se dice en una línea en vez de preguntarlo (verificación de hoy70) */}
            {forma.queCotiza !== 'peak_shaving' && (forma.esNuevo === false || (forma.esNuevo === true && !aislado)) && (
              <Segmentado<'mensual' | 'bimestral' | 'no_se'>
                etiqueta="¿Cada cuánto le llega el recibo?"
                valor={forma.facturacion}
                alCambiar={(v) => poner('facturacion', v)}
                opciones={[
                  { valor: 'mensual', texto: 'Mensual' },
                  { valor: 'bimestral', texto: 'Bimestral' },
                  { valor: 'no_se', texto: 'No sé' },
                ]}
                columnas={3}
                ayuda={forma.esNuevo ? 'Si no sabe: en tarifa doméstica (1 a 1F y DAC) se cotiza bimestral, como cobra CFE; en las demás, mensual.' : undefined}
                error={err('facturacion')}
              />
            )}
            {forma.esNuevo === false && (
              <>
                {forma.queCotiza === 'peak_shaving' ? (
                  <p className="nota-suave">Tarifa GDMTH, recibo mensual: el recorte de punta es solo para GDMTH.</p>
                ) : (
                  <CampoSelect
                    etiqueta="Tarifa"
                    value={forma.tarifa}
                    onChange={(e) => poner('tarifa', e.target.value as Tarifa | 'no_se')}
                    opciones={tarifasOpciones}
                    // ADENDA z52-1: un negocio con recibo de casa se cotiza en PDBT (o GDBT si se escoge aquí)
                    ayuda={paraTarifa === 'comercio' || paraTarifa === 'industria' ? 'Si su recibo es de casa (1F), con paneles se cotiza en PDBT; escoge GDBT si su carga pasa de 25 kW.' : undefined}
                  />
                )}
                {preguntaTransformador}
                {forma.queCotiza === 'peak_shaving' ? (
                  // verificación de hoy70: el recorte SÍ pide los 12 (la punta de cada mes); decir «no es obligatorio» hacía volver con el cliente
                  <div className="aviso-ancho aviso-ancho--ambar" role="note">
                    <strong>{MENSAJE_RECORTE_PIDE_12}</strong> Pídeselos al cliente desde ahora (PDF de CFE o foto de cada uno).
                  </div>
                ) : recibos === 12 ? (
                  <div className="aviso-ancho aviso-ancho--ambar" role="note">
                    <strong>Sube los recibos que tenga el cliente (lo ideal, los 12 de cada medidor).</strong> En {forma.tarifa} el historial impreso no trae los importes ni el reparto base, intermedia y punta: con los que haya se cotiza y los meses que falten se estiman.
                  </div>
                ) : forma.tipo === 'industria' && forma.tarifa === 'no_se' ? (
                  <p className="nota-suave">Si resulta GDMTH, sube los recibos que tenga (lo ideal, 12). En GDMTO basta el último.</p>
                ) : null}
              </>
            )}
            {/* z58: el consumo del cliente nuevo va al final del paso: «Usar estos consumos» lo autoriza y sigue. */}
            {forma.esNuevo && (
              <div className="grupo-condicional">
                <Segmentado<FormaCuestionario['metodoEstimacion']>
                  etiqueta="¿Cómo estimamos su consumo?"
                  valor={forma.metodoEstimacion}
                  alCambiar={(v) => poner('metodoEstimacion', v)}
                  opciones={[
                    // z11: se describe en texto y la IA estima; z58: también en híbrido y aislado (el aislado ya no repite sus
                    // equipos en un segundo cuadro de cargas: la descripción sale de ellos).
                    { valor: 'cuadro_cargas', texto: 'Calcular con IA', detalle: 'Describe y la IA estima' },
                    { valor: 'kwh_mes', texto: 'kWh al mes', detalle: 'Ya trae un número' },
                    // z46-2 (P22): el cliente solo sabe cuánto paga (un aislado no tiene recibo de CFE).
                    ...(aislado ? [] : [{ valor: 'pago' as const, texto: 'Lo que paga', detalle: 'Solo sabe cuánto paga' }]),
                  ]}
                  columnas={aislado ? 2 : 3}
                />
                {forma.metodoEstimacion === 'pago' ? (
                  <>
                    <CampoTexto etiqueta="¿Cuánto paga a CFE?" inputMode="decimal" sufijo={forma.pagoPeriodo === 'bimestral' ? 'pesos al bimestre' : 'pesos al mes'} value={forma.pagoMonto} onChange={(e) => poner('pagoMonto', e.target.value)} error={err('pagoMonto')} />
                    <Segmentado<FormaCuestionario['pagoPeriodo']>
                      etiqueta="¿Cada cuándo le llega?"
                      valor={forma.pagoPeriodo}
                      alCambiar={(v) => poner('pagoPeriodo', v)}
                      opciones={[
                        { valor: 'bimestral', texto: 'Al bimestre' },
                        { valor: 'mensual', texto: 'Al mes' },
                      ]}
                      columnas={2}
                    />
                    <p className="nota-suave">Con IVA, como viene en su recibo. El cotizador saca sus kWh con las cuotas de CFE de su tarifa ({forma.tarifa === 'no_se' ? 'si no la sabes, la de su ciudad: 1F en Hermosillo' : forma.tarifa}) y la propuesta dice «Estimado con lo que pagas».</p>
                  </>
                ) : forma.metodoEstimacion !== 'cuadro_cargas' ? (
                  <>
                    <CampoTexto etiqueta="Consumo estimado" inputMode="decimal" sufijo="kWh / mes" value={forma.kwhMes} onChange={(e) => poner('kwhMes', e.target.value)} error={err('kwhMes')} />
                    {/* aislado: los paneles salen de este consumo; se propone el de sus equipos (verificación adversarial del 14-oct) */}
                    {aislado && kwhEquipos !== null && kwhEquipos > 0 && !((numeroDe(forma.kwhMes) ?? 0) >= kwhEquipos) && (
                      <div className="fila-botones fila-botones--entre">
                        <span className="nota-suave">Sus equipos usan unos {numero(kwhEquipos)} kWh al mes; con menos, los paneles no alcanzan y el PDF no sale.</span>
                        <Boton chico onClick={() => poner('kwhMes', String(kwhEquipos))}>{`Usar ${kwhEquipos} kWh al mes`}</Boton>
                      </div>
                    )}
                  </>
                ) : (
                  <EstimarConIA
                    tipo={tarifaDeNegocio ? 'comercio' : 'casa'}
                    tipoProyecto={forma.tipo}
                    cargas={forma.cargas}
                    fuente={forma.fuenteEstimacion}
                    error={err('cargas')}
                    sugerencia={aislado ? descripcionDeEquipos(forma.respaldoCargas) : ''}
                    minimoKwhMes={kwhEquipos}
                    autorizado={forma.cargas.length > 0 && forma.consumosAutorizados === firmaConsumos(forma.tipo, forma.cargas)}
                    alCambiar={(cargas, fuente) => {
                      poner('cargas', cargas);
                      if (fuente !== undefined) poner('fuenteEstimacion', fuente);
                    }}
                    alUsar={usarConsumos}
                  />
                )}
                {/* z12: cada pregunta, adaptada al tipo de cliente (nada de «negocio igual» en una casa). Con la descripción, la
                    fuente es la descripción misma. */}
                {forma.metodoEstimacion !== 'cuadro_cargas' && forma.metodoEstimacion !== 'pago' && (
                  <CampoTexto
                    etiqueta="¿De dónde sale la estimación?"
                    placeholder={tarifaDeNegocio ? 'Ej. lo dijo el dueño; negocio igual en la misma calle' : 'Ej. lo dijo el dueño; lo que pagaba en su casa anterior'}
                    maxLength={300}
                    value={forma.fuenteEstimacion}
                    onChange={(e) => poner('fuenteEstimacion', e.target.value)}
                    error={err('fuenteEstimacion')}
                  />
                )}
                {forma.metodoEstimacion === 'cuadro_cargas' && err('fuenteEstimacion') && <p className="campo__error">{err('fuenteEstimacion')}</p>}
                {tarifaDeNegocio && (
                  // Hallazgo del 10-oct: un negocio o industria nueva no dejaba escoger la tarifa y se calculaba siempre
                  // como PDBT (una industria de 171 kWp prometía ahorros PDBT).
                  <CampoSelect
                    etiqueta="Tarifa que va a tener"
                    value={forma.tarifa}
                    onChange={(e) => poner('tarifa', e.target.value as Tarifa | 'no_se')}
                    opciones={[
                      { valor: 'no_se', texto: tarifaDeIndustria ? 'No sé (se calcula como GDMTO)' : 'No sé (se calcula como PDBT)' },
                      ...TARIFAS_NEGOCIO.map((x) => ({ valor: x, texto: `${x} · ${x.startsWith('GDMT') ? 'media tensión' : 'baja tensión'}` })),
                    ]}
                    ayuda="PDBT es hasta 25 kW de demanda; arriba, GDBT o media tensión (GDMTO/GDMTH). Si no sabes, pregúntale a Diego."
                  />
                )}
                {negocio && preguntaTransformador}
              </div>
            )}
          </div>
        )}

        {paso === 'notas' && (
          <div className="pila">
            <CampoArea
              etiqueta="Todo lo que sabes del cliente"
              ayuda={amp ? `Quedan en la cotización. ${numero(forma.notas.length)} de 4,000 letras.` : `Quedan en la cotización; si hay IA, las lee junto con los recibos. ${numero(forma.notas.length)} de 4,000 letras.`}
              rows={7}
              maxLength={4000}
              placeholder={amp ? 'A qué hora se queda sin luz, qué tenía prendido, si tiene monitoreo, cuándo quiere instalar…' : 'Planes, dudas del cliente, cómo es el techo, cuándo quiere instalar…'}
              value={forma.notas}
              onChange={(e) => poner('notas', e.target.value)}
              error={err('notas')}
            />
            <div className="ideas" aria-label="Ideas para agregar">
              {(amp ? IDEAS_NOTAS_AMPLIACION : IDEAS_NOTAS).map((idea) => (
                <button key={idea} type="button" className="pastilla pastilla--idea" onClick={() => poner('notas', (forma.notas.trim() ? `${forma.notas.trim()} ` : '') + idea)}>
                  <Plus aria-hidden /> {idea}
                </button>
              ))}
            </div>
            <Resumen
              forma={forma}
              conPiezas={!original}
              bateria={bateriaAmp}
              recibos={recibos}
              extra={[
                ...(vendedorId ? [['A nombre de', `${vendedores.find((v) => v.id === vendedorId)?.nombre ?? 'otro vendedor'} (tú quedas como «hecha por»)`] as [string, string]] : []),
                ...(sitios ? [['Sucursales', `${sitios.length}: ${textoSitiosResumen(forma, sitios)} · una cotización por sucursal`] as [string, string]] : []),
              ]}
              sucursales={sitios?.length ?? 0}
              advertencias={sinWhatsapp.includes(vendedorId || usuario.id) ? [`${mensajeFaltaWhatsapp(vendedores.find((v) => v.id === (vendedorId || usuario.id))?.nombre ?? usuario.nombre)} Sin él, el PDF de esta cotización no sale.`] : []}
            />
          </div>
        )}

        <ErrorEnLinea error={error} />
      </div>

      <div className="barra-acciones">
        {indice > 0 ? (
          <Boton variante="fantasma" icono={<ArrowLeft aria-hidden />} onClick={() => irA(PASOS[indice - 1]!)}>
            Atrás
          </Boton>
        ) : !original && (forma.nombre || forma.tipo) ? (
          // Hallazgo del 10-oct: borraba los 4 pasos con un toque, junto a "Siguiente". Ahora pregunta.
          <BotonConfirmar pregunta="¿Borrar todo lo capturado?" alConfirmar={empezarDeNuevo}>
            Empezar de nuevo
          </BotonConfirmar>
        ) : original ? (
          <Enlace a={`/cotizaciones/${original.id}`} className="boton boton--fantasma">
            <ArrowLeft aria-hidden />
            <span>Volver sin guardar</span>
          </Enlace>
        ) : (
          <span />
        )}
        <Boton variante="principal" cargando={enviando} onClick={() => siguiente()} icono={paso === 'notas' ? <Check aria-hidden /> : <ArrowRight aria-hidden />} iconoDespues={paso !== 'notas'}>
          {paso !== 'notas' ? 'Siguiente' : original ? 'Guardar cambios' : 'Crear cotización'}
        </Boton>
      </div>
    </div>
  );
}

/** extra: lo que no vive en el cuestionario y también se crea con el botón (recorrido del 13-oct: a nombre de otro vendedor
 *  y las sucursales no salían en «Revisa antes de crear»). */
export function Resumen({
  forma,
  recibos,
  extra = [],
  advertencias = [],
  sucursales = 0,
  conPiezas = true,
  bateria = null,
}: {
  forma: FormaCuestionario;
  recibos: number;
  extra?: readonly [string, string][];
  advertencias?: readonly string[];
  sucursales?: number;
  /** Ampliación de baterías: si se valida también la batería y las cantidades (en el alta) y la batería elegida. */
  conPiezas?: boolean;
  bateria?: BateriaVista | null;
}) {
  const r = validar(forma, 'notas', conPiezas);
  const c = r.cuestionario;
  if (!c) return null;
  if (c.tipo === 'ampliacion_baterias') return <ResumenAmpliacion forma={forma} c={c} conPiezas={conPiezas} bateria={bateria} extra={extra} advertencias={advertencias} />;
  // TODO SE ELIGE DESDE EL INICIO (13-oct, 22:45): lo que se eligió en «Qué cotizas», arriba de todo.
  const enfoque = forma.clienteTipo && forma.queCotiza ? forma : { ...forma, ...enfoqueDe(forma) };
  const especiales = [
    ...(sucursales > 1 ? [`Varios sitios (${sucursales})`] : []),
    ...(c.dos_medidores ? ['Dos recibos / dos medidores'] : []),
    ...(c.ampliacion.es_ampliacion ? ['Ampliación'] : []),
    ...(c.consumo.es_nuevo && c.tipo !== 'aislado' ? ['Cliente nuevo'] : []),
    ...(c.consumo.cambio === 'aumenta' ? ['Va a subir el consumo'] : []),
  ];
  const filas: [string, string][] = [
    ['Cliente', `${c.cliente.nombre} · ${c.cliente.ciudad}, ${c.cliente.estado}`],
    ...(c.tipo === 'aislado' ? [] : [['Recibo a nombre de', c.titular.es_el_cliente ? 'El cliente' : (c.titular.nombre ?? '—')] as [string, string]]),
    ['Qué se cotiza', `${enfoque.clienteTipo ? ETIQUETA_CLIENTE[enfoque.clienteTipo] : '—'} · ${enfoque.queCotiza ? ETIQUETA_QUE[enfoque.queCotiza] : '—'}${c.cliente.giro ? ` · ${c.cliente.giro}` : ''}`],
    ['Casos especiales', especiales.length ? especiales.join(' · ') : 'Ninguno'],
    ...(c.fiscal ? [['Régimen fiscal', `${ETIQUETA_REGIMEN_FISCAL[c.fiscal.regimen]}${c.fiscal.regimen === 'pf_empresarial' ? ` · ISR ${c.fiscal.tasa_isr_pct} %` : ''}`] as [string, string]] : []),
    ['Etapa', etapaDe(c) === 'final' ? 'Cotización final (visita hecha)' : 'Precotización (sin visita)'],
    ['Consumo', c.consumo.es_nuevo ? 'Nuevo: se estima' : `${c.dos_medidores ? 'Dos medidores' : 'Un medidor'} · ${c.facturacion === 'no_se' ? 'facturación por confirmar' : c.facturacion} · tarifa ${c.tarifa === 'no_se' ? 'por leer' : c.tarifa}`],
    // verificación del 28-oct: con el aumento aceptado, también sus kWh (antes solo la descripción)
    ['Cambio de consumo', c.consumo.cambio === 'igual' ? 'Sin cambio' : `${c.consumo.cambio === 'aumenta' ? 'Sube' : 'Baja'}${c.consumo.aumento ? ` +${numero(c.consumo.aumento.kwh_mes.reduce((a, b) => a + b, 0))} kWh al año (${c.consumo.aumento.motivo})` : c.consumo.consumo_futuro_kwh_anual ? ` a ${numero(c.consumo.consumo_futuro_kwh_anual)} kWh al año` : ''}${c.consumo.cambio_detalle ? `: ${c.consumo.cambio_detalle}` : ''}`],
    // «nuevos» solo en el recorte de punta (el cliente puede ya tener paneles); las baterías sin paneles dicen lo de producción
    // (verificación de hoy75: hoy70 había cambiado también ese texto sin decirlo)
    ['Estructura', c.recorte_punta ? 'Sin paneles nuevos (no aplica)' : c.respaldo?.con_paneles === false ? 'Sin paneles (no aplica)' : ETIQUETA_ESTRUCTURA[c.estructura ?? 'losa_lamina']],
    mediaTensionForma(forma)
      ? ['Transformador', c.servicio?.fases === 'trifasico' ? (c.servicio.tension ? `${c.servicio.tension} V` : `${ETIQUETA_NO_SE_TRANSFORMADOR} (se calcula con 220 V)`) : 'Falta contestar'] as [string, string]
      : c.tipo === 'aislado'
        ? ['Servicio de CFE', 'Sin CFE (aislado)']
        : ['Servicio de CFE', c.servicio ? `${ETIQUETA_FASES[c.servicio.fases].split(' (')[0]}${c.servicio.tension ? ` · ${c.servicio.tension} V` : ' · tensión por confirmar'}` : 'Lo deduce el cotizador (confirmar en la visita)'],
    ['Ampliación', c.ampliacion.es_ampliacion ? `Sí${c.ampliacion.existente?.kwp ? ` · ya tiene ${c.ampliacion.existente.kwp} kWp` : c.ampliacion.existente?.paneles ? ` · ya tiene ${c.ampliacion.existente.paneles} paneles` : ''}` : 'No'],
  ];
  filas.push(...extra);
  if (!c.consumo.es_nuevo) filas.push(['Recibos que vas a subir', `${recibos} por medidor${c.dos_medidores ? ' (dos medidores)' : ''}`]);
  if (c.tipo === 'baterias' || c.tipo === 'aislado') {
    const r = c.respaldo;
    const w = r ? r.cargas.reduce((s, x) => s + x.cantidad * x.w, 0) : 0;
    filas.push([
      'Respaldo',
      r
        ? r.tipo === 'aislado'
          ? `${r.cargas.length === 1 ? '1 equipo' : `${r.cargas.length} equipos`} · ${numero(w / 1000, 2)} kW · aislado${r.con_paneles ? ' con paneles' : ' sin paneles'}`
          : `Toda la casa (${r.tipo === 'hibrido' ? '1 a 2 h por opción' : 'unas 2, 4 y 8 h'} con el consumo del recibo) · ${r.tipo === 'hibrido' ? 'híbrido' : 'respaldo'}${r.con_paneles ? ' con paneles' : ' sin paneles'}`
        : 'Sin equipos: los niveles del aislado no se calculan',
    ]);
  }
  // Verificación adversarial del 14-oct: híbrido y aislado se crean y calculan, pero el PDF todavía no sale; se dice AQUÍ (antes de
  // capturar recibo, revisión, costos y opciones), con el mismo texto con el que el generador lo rechaza.
  const sinPdf = pdfTodaviaNoSale(c);
  const avisosResumen = [...(sinPdf ? [`El PDF de esta cotización todavía no sale (se crea y se calcula igual). ${sinPdf}`] : []), ...advertencias];
  return (
    <div className="resumen">
      <p className="resumen__titulo">Revisa antes de crear</p>
      <dl className="resumen__lista">
        {filas.map(([k, v]) => (
          <div key={k} className="resumen__fila">
            <dt>{k}</dt>
            <dd>{v}</dd>
          </div>
        ))}
      </dl>
      {avisosResumen.map((t) => (
        <p key={t} className="aviso aviso--amarillo" role="note">
          {t}
        </p>
      ))}
    </div>
  );
}

/** «Revisa antes de crear» de una ampliación de baterías: cliente, para quién, etapa y el sistema que se amplía. */
function ResumenAmpliacion({
  forma,
  c,
  conPiezas,
  bateria,
  extra,
  advertencias,
}: {
  forma: FormaCuestionario;
  c: { cliente: { nombre: string; ciudad: string; estado: string }; visita_tecnica_hecha: boolean };
  conPiezas: boolean;
  bateria: BateriaVista | null;
  extra: readonly [string, string][];
  advertencias: readonly string[];
}) {
  const rango = rangoAmpliacion(bateria, numeroDe(forma.ampActuales), numeroDe(forma.ampNuevas));
  const filas: [string, string][] = [
    ['Cliente', `${c.cliente.nombre} · ${c.cliente.ciudad}, ${c.cliente.estado}`],
    ['Qué se cotiza', `${forma.clienteTipo ? ETIQUETA_CLIENTE[forma.clienteTipo] : '—'} · ${ETIQUETA_QUE.ampliacion_baterias}`],
    ['Etapa', c.visita_tecnica_hecha ? 'Cotización final (visita hecha)' : 'Precotización (sin visita)'],
    ...(conPiezas && bateria
      ? ([
          ['Batería', `${bateria.marca} ${bateria.modelo}`],
          ['Capacidad', rango ? `Tiene ${forma.ampActuales.trim()} y suma ${forma.ampNuevas.trim()}: de ${kwhCorto(rango.hoy)} a ${kwhCorto(rango.con)} kWh (${rango.total} de ${rango.maximo} en paralelo)` : '—'],
          ['Costo de las baterías', bateria.costo_en_tabla ? 'De la tabla de precios' : 'Del proveedor: lo tecleas en la cotización'],
        ] as [string, string][])
      : []),
    ...extra,
  ];
  return (
    <div className="resumen">
      <p className="resumen__titulo">Revisa antes de crear</p>
      <dl className="resumen__lista">
        {filas.map(([k, v]) => (
          <div key={k} className="resumen__fila">
            <dt>{k}</dt>
            <dd>{v}</dd>
          </div>
        ))}
      </dl>
      {conPiezas && <p className="nota-suave">Al crearla se abre «Ampliación»: ahí capturas los costos (el precio lo calcula el cotizador) y, si quieres, la hoja 2 con la noche medida.</p>}
      {advertencias.map((t) => (
        <p key={t} className="aviso aviso--amarillo" role="note">
          {t}
        </p>
      ))}
    </div>
  );
}
